import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState('verifying');
  // 'verifying' | 'error' | 'no_token'

  const verifiedRef = useRef(false);

  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('no_token');
      return;
    }

    // Remove the credential from the address bar before anything else runs — it
    // must not persist in history or leak via Referer. The token is already
    // held in the local const above, so the exchange below is unaffected.
    window.history.replaceState({}, '', window.location.pathname);

    localStorage.removeItem('token');

    async function verify() {
      try {
        const response = await fetch(`${API_URL}/auth/verify-magic-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          setStatus('error');
          return;
        }

        const data = await response.json();

        const userResponse = await fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${data.access_token}` },
        });

        if (!userResponse.ok) {
          setStatus('error');
          return;
        }

        const userData = await userResponse.json();

        try {
          const calcInputsJson = localStorage.getItem('calculator_inputs');
          if (calcInputsJson) {
            const calcInputs = JSON.parse(calcInputsJson);
            const setupBody = {
              annual_income: calcInputs.annual_income,
              state: calcInputs.state,
              filing_status: calcInputs.filing_status,
              business_expenses: calcInputs.business_expenses || 12000,
            };
            if (calcInputs.prior_year_tax) {
              setupBody.prior_year_tax = calcInputs.prior_year_tax;
            }
            await fetch(`${API_URL}/tax/setup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.access_token}`,
              },
              body: JSON.stringify(setupBody),
            });
            localStorage.removeItem('calculator_inputs');
          }
        } catch (err) {
          console.error('Failed to sync calculator inputs:', err);
        }

        login(userData, data.access_token);
        navigate('/dashboard');
      } catch (err) {
        setStatus('error');
      }
    }

    verify();
  }, [searchParams, navigate, login]);

  const pageStyle = {
    minHeight: '100vh',
    backgroundColor: 'var(--paper)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'var(--font-sans)',
    padding: '24px',
  };

  const cardStyle = {
    backgroundColor: 'var(--card)',
    borderRadius: 'var(--r-card)',
    padding: '40px',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    boxShadow: 'var(--sh-raise)',
  };

  const logoStyle = {
    fontFamily: 'var(--font-serif)',
    fontSize: '32px',
    color: 'var(--ink)',
    marginBottom: '24px',
  };

  const ctaLinkStyle = {
    display: 'inline-block',
    backgroundColor: 'var(--signal)',
    color: 'var(--on-signal)',
    padding: '12px 24px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
  };

  const Logo = () => (
    <div style={logoStyle}>
      Tru<span style={{ color: 'var(--signal)' }}>Flow</span>
    </div>
  );

  if (status === 'verifying') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <p style={{ fontSize: '16px', color: 'var(--slate)' }}>
            Signing you in...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'no_token') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', color: 'var(--ink)', marginBottom: '12px' }}>
            No sign-in link found
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--slate)', lineHeight: 1.6, marginBottom: '24px' }}>
            This page is for verifying email sign-in links. If you got here
            by mistake, you can return to the homepage.
          </p>
          <Link to="/" style={ctaLinkStyle}>Go to homepage</Link>
        </div>
      </div>
    );
  }

  // status === 'error'
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Logo />
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', color: 'var(--ink)', marginBottom: '12px' }}>
          Link expired or invalid
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--slate)', lineHeight: 1.6, marginBottom: '24px' }}>
          This sign-in link is no longer valid. Magic links expire after 15
          minutes and can only be used once. Request a new one from the
          homepage.
        </p>
        <Link to="/" style={ctaLinkStyle}>Get a new link</Link>
      </div>
    </div>
  );
}

export default AuthVerify;
